/**
 * 66.15 (DECISIONS 183 (5); the owner on the valley standoff: *"it looks really cool and I don't want to mess with it"*,
 * and turn it into coherent strategy): the standoff manoeuvre, behind `GameConfig.standoffManoeuvre` (off unless true; one
 * value or per side). sweeps/2026-10-14-valley-standoff.md measured the standoff as the normal fight and its incursions as
 * rare, ordered and alone. Built on its sketch:
 *
 * 1. **The standoff, a local state in the commander's read.** A squad whose nearest known enemy (its side's picture, the
 *    bots' floor) stays between `longPeople` and `farPeople` for `holdSeconds` is in a standoff. Two such squads or more
 *    facing one enemy group are a stretch of line in a standoff.
 * 2. **The base of fire is the squads already in it**, left where they are and firing as they do.
 * 3. **One manoeuvre squad goes round by dead ground.** Its waypoint is a spot off the line toward the enemy, where the ground
 *    hides it from the enemy group's eye (66.11's `terrainSpotsNear`, tried on either side). It walks there and waits.
 * 4. **It commits only on an advantage** — `localForce` (182's local read, scoped to this use: by place, never side-wide)
 *    round the enemy group: its own living plus our people there, over their believed number, at least `commitRatio`.
 *    Then the commander forms a play at the enemy group with the base named as the support, firing from where it stands
 *    (`here`), and the manoeuvre as the assault, jumping off from its waypoint: the play's phases time the support's fire
 *    to the move (set at once, the bound, the lift as it closes). No advantage by `waitSeconds`: it goes back to its
 *    orders and the standoff stands.
 *
 * One manoeuvre a side at a time; `cooldownSeconds` between. Pure but for the planner's own memory (the commander's).
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import type { Squad, IssueOrder, FormPlay } from './squadOrders';
import { teamPicture } from '../core/picture';
import { terrainSpotsNear } from '../core/coverMap';
import { personPointOn } from '../core/fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { squadTaskFor } from '../core/squads';
import { PLAY } from '../core/plays';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from '../core/dmath';
import { PHYS_DT } from '../constants';

export const STANDOFF = {
  /** A squad's nearest known enemy no nearer than this (people: the far edge of the bots' fight band) … */
  longPeople: 15,
  /** … and no farther than this: a squad in a standoff. */
  farPeople: 90,
  /** That steadily for this long (s): the standoff is the state, not a passing look. */
  holdSeconds: 10,
  /** The beliefs counted (the bots' floor, TEAM_TRACK_MIN_CONFIDENCE). */
  minConfidence: 0.25,
  /** The enemy group: the beliefs within this (people) of the one nearest the manoeuvre squad; the local read's radius. */
  enemyPeople: 20,
  /** The base of fire: the squads in a standoff facing the same enemy group — their nearest belief within this (people) of
   *  the group's. */
  basePeople: 80,
  /** The manoeuvre: another squad of the side within this (people) of the base, not in contact itself (on the slice's wide
   *  board a stretch of line is mostly one squad facing one group: two standoff squads together were 160-230 people apart). */
  manoeuvrePeople: 150,
  /** The waypoint: this far off the line (people), at this share of the way toward the enemy group … */
  flankPeople: 30,
  flankAlong: 0.5,
  /** … searched for dead ground within this (people). */
  spotPeople: 15,
  /** At the waypoint within this (people). */
  arrivePeople: 5,
  /** Commit when (the manoeuvre's living + ours near the enemy group) / their believed number is at least this. */
  commitRatio: 1.5,
  /** The route's, the wait's and the cooldown's lengths (s). */
  routeSeconds: 90,
  waitSeconds: 60,
  cooldownSeconds: 60,
};

/** Is the standoff manoeuvre on for `team` (one value or its side's; off unless true)? */
export function standoffOn(state: Pick<GameState, 'config'>, team: number): boolean {
  const v = state.config.standoffManoeuvre;
  return Array.isArray(v) ? v[team] === true : v === true;
}

// 66.16: the local read lives in core (core/localForce.ts) — the Match's plays read it too
import { localForce } from '../core/localForce';
export { localForce, type LocalForce } from '../core/localForce';

/** The step from `a` toward `b` by `angle` radians along the great circle, then `side` radians to its left. */
function offset(a: Vec3, b: Vec3, along: number, side: number): Vec3 {
  const ua = vecNormalize(a), ub = vecNormalize(b);
  const t0 = vecSub(ub, vecScale(ua, vecDot(ub, ua)));
  const dir = vecLength(t0) > 1e-12 ? vecNormalize(t0) : vecNormalize(vecSub({ x: 1, y: 0, z: 0 }, vecScale(ua, ua.x)));
  const p = vecNormalize(vecAdd(vecScale(ua, dcos(along)), vecScale(dir, dsin(along))));
  const left = vecNormalize({ x: p.y * dir.z - p.z * dir.y, y: p.z * dir.x - p.x * dir.z, z: p.x * dir.y - p.y * dir.x });
  return vecNormalize(vecAdd(vecScale(p, dcos(side)), vecScale(left, dsin(side))));
}

export interface StandoffOp {
  squadId: number;
  base: number[];
  enemy: { planetIndex: number; surfaceDir: Vec3 };
  waypoint: Vec3;
  phase: 'route' | 'wait' | 'assault';
  since: number;
  /** How it ended (for the measures): 'played' (its play done), 'died', 'masked' (another order took the squad), 'route' (never got there), 'waited' (no advantage). */
  end?: { tick: number; why: 'played' | 'died' | 'masked' | 'route' | 'waited' };
  ratioAtCommit?: number;
}

/** The commander's standoff planner for one side: its memory (who has been in a standoff since when, the manoeuvre under way). */
export function createStandoffPlanner(team: number) {
  const since = new Map<number, number>();
  const ops: StandoffOp[] = [];
  let op: StandoffOp | null = null, cooldownUntil = 0;
  const living = (st: GameState, sq: Squad) => sq.unitIds.map(id => st.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p && p.alive);
  const centre = (st: GameState, sq: Squad): Vec3 | null => {
    const us = living(st, sq).filter(u => !u.mount);
    if (!us.length) return null;
    const s = us.reduce((a, u) => vecAdd(a, vecNormalize(u.surfaceDir)), { x: 0, y: 0, z: 0 });
    return vecLength(s) > 1e-9 ? vecNormalize(s) : null;
  };
  const finish = (tick: number, why: NonNullable<StandoffOp['end']>['why']) => { if (op) { op.end = { tick, why }; op = null; cooldownUntil = tick + Math.round(STANDOFF.cooldownSeconds / PHYS_DT); } };

  /** Once a second: the standoff read, then the manoeuvre's next step. `humanOrder`: a squad with a human's order in force is the human's. */
  function step(st: GameState, squads: readonly Squad[], tick: number, issue: IssueOrder, formPlay: FormPlay, humanOrder: (sq: Squad) => boolean): void {
    const person = PERSON_BODY_HEIGHT * personSetOf(st).k;
    const beliefs = teamPicture(st, team, tick).filter(b => b.confidence >= STANDOFF.minConfidence);
    const arc = (pi: number, a: Vec3, b: Vec3) => (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * st.planets[pi].radius) / person;
    const centres = new Map<number, Vec3>();
    for (const sq of squads) {
      const c = centre(st, sq);
      const pi = living(st, sq)[0]?.planetIndex;
      if (!c || pi === undefined) { since.delete(sq.id); continue; }
      centres.set(sq.id, c);
      let d = Infinity;
      for (const b of beliefs) if (b.planetIndex === pi) d = Math.min(d, arc(pi, c, b.surfaceDir));
      if (d >= STANDOFF.longPeople && d <= STANDOFF.farPeople) { if (!since.has(sq.id)) since.set(sq.id, tick); } else since.delete(sq.id);
    }

    if (op) {
      const sq = squads.find(q => q.id === op!.squadId);
      const lead = sq ? living(st, sq)[0] : undefined;
      if (!sq || !lead) { finish(tick, 'died'); return; }
      if (humanOrder(sq)) { finish(tick, 'masked'); return; }
      const task = squadTaskFor(st, lead.id, tick);
      const c = centres.get(sq.id);
      if (op.phase === 'assault') {
        const play = (st.plays ?? []).find(p => p.team === team && p.here && p.assault.includes(sq.id) && p.issuedTick >= op!.since);
        if (!play || play.phase === 'done') finish(tick, 'played');
        return;
      }
      const ours = !!task && (task.order.kind === 'move' || task.order.kind === 'hold') && 'surfaceDir' in task.order && !!task.order.surfaceDir
        && arc(op.enemy.planetIndex, task.order.surfaceDir, op.waypoint) < 0.5;
      // (on the route: there — a move task leaves the squad as its first members arrive, so arrival is read before the task;
      // the route's own task run out is the route's end, not another order's)
      const there = !!c && arc(op.enemy.planetIndex, c, op.waypoint) <= (ours ? STANDOFF.arrivePeople : 3 * STANDOFF.arrivePeople);
      if (op.phase === 'route' && !ours && !there) { finish(tick, (tick - op.since) * PHYS_DT >= STANDOFF.routeSeconds - 1 ? 'route' : 'masked'); return; }
      if (op.phase !== 'route' && !ours) { finish(tick, 'masked'); return; }
      if (op.phase === 'route') {
        if (there) {
          issue(sq, { kind: 'hold', planetIndex: op.enemy.planetIndex, surfaceDir: op.waypoint }, 'task', tick + Math.round(STANDOFF.waitSeconds / PHYS_DT));
          op.phase = 'wait'; op.since = tick;
        } else if ((tick - op.since) * PHYS_DT > STANDOFF.routeSeconds) finish(tick, 'route');
        return;
      }
      // waiting at the waypoint: the group followed (its beliefs round the old centre, re-centred), then commit on an advantage
      // round it, else hold till the wait is over
      const round = beliefs.filter(b => b.planetIndex === op!.enemy.planetIndex && arc(op!.enemy.planetIndex, b.surfaceDir, op!.enemy.surfaceDir) <= STANDOFF.enemyPeople);
      if (round.length) op.enemy = { planetIndex: op.enemy.planetIndex, surfaceDir: vecNormalize(round.reduce((a, b) => vecAdd(a, vecNormalize(b.surfaceDir)), { x: 0, y: 0, z: 0 })) };
      const lf = localForce(st, team, op.enemy, STANDOFF.enemyPeople, tick);
      // (a live contact only: someone believed there, freshly — 68.22's own window. Committing on thin, stale beliefs read as an
      // easy advantage, and the play, finding its objective quiet, dissolved into holding the enemy's old ground)
      const live = lf.enemies >= 0.5 && lf.freshest !== null && lf.freshest <= PLAY.knownSeconds;
      if (lf.freshest === null) { finish(tick, 'waited'); return; }   // nothing known there now: the standoff has gone
      const ratio = (living(st, sq).length + lf.friends) / Math.max(lf.enemies, 0.5);
      const base = squads.filter(q => op!.base.includes(q.id) && living(st, q).length > 0 && !humanOrder(q));
      if (live && ratio >= STANDOFF.commitRatio && base.length > 0 && formPlay([...base, sq], op.enemy, { support: base, here: true })) {
        op.phase = 'assault'; op.since = tick; op.ratioAtCommit = +ratio.toFixed(2);
      } else if ((tick - op.since) * PHYS_DT > STANDOFF.waitSeconds) finish(tick, 'waited');
      return;
    }

    if (tick < cooldownUntil) return;
    // the base: squads in a standoff long enough, not a human's, not in a play, two people standing at least, whose enemy
    // group the side sees now (a fresh belief: 68.22's window)
    const inPlay = (sq: Squad) => living(st, sq).some(u => squadTaskFor(st, u.id, tick)?.play !== undefined);
    const free = (sq: Squad) => living(st, sq).filter(u => !u.mount).length >= 2 && !humanOrder(sq) && !inPlay(sq);
    const ready = squads.filter(sq => { const t = since.get(sq.id); return t !== undefined && (tick - t) * PHYS_DT >= STANDOFF.holdSeconds && free(sq); });
    if (!ready.length) return;
    const first = [...ready].sort((a, b) => living(st, b).length - living(st, a).length || a.id - b.id)[0];
    const bc = centres.get(first.id)!, pi = living(st, first)[0].planetIndex, planet = st.planets[pi];
    const nearestTo = (c: Vec3) => beliefs.filter(b => b.planetIndex === pi).sort((a, b) => arc(pi, c, a.surfaceDir) - arc(pi, c, b.surfaceDir))[0];
    const near = nearestTo(bc);
    if (!near || (tick - near.tick) * PHYS_DT > PLAY.knownSeconds) return;
    const base = ready.filter(q => { const b = nearestTo(centres.get(q.id)!); return !!b && arc(pi, b.surfaceDir, near.surfaceDir) <= STANDOFF.basePeople; });
    // the manoeuvre: the strongest other free squad near the base, not in contact itself (its nearest known enemy past the band)
    const contact = (sq: Squad) => { const c = centres.get(sq.id); const b = c ? nearestTo(c) : undefined; return !!b && !!c && arc(pi, c, b.surfaceDir) < STANDOFF.longPeople; };
    const man = squads.filter(q => !base.includes(q) && free(q) && centres.has(q.id) && living(st, q)[0].planetIndex === pi && arc(pi, centres.get(q.id)!, bc) <= STANDOFF.manoeuvrePeople && !contact(q))
      .sort((a, b) => living(st, b).length - living(st, a).length || arc(pi, centres.get(a.id)!, bc) - arc(pi, centres.get(b.id)!, bc) || a.id - b.id)[0];
    if (!man) return;
    const mc = centres.get(man.id)!;
    // the enemy group: the beliefs round the one the base faces
    const group = beliefs.filter(b => b.planetIndex === pi && arc(pi, b.surfaceDir, near.surfaceDir) <= STANDOFF.enemyPeople);
    const enemyDir = vecNormalize(group.reduce((acc, b) => vecAdd(acc, vecNormalize(b.surfaceDir)), { x: 0, y: 0, z: 0 }));
    // the waypoint: dead ground off the line on either side, the nearer to its flank point
    const ps = personSetOf(st), eye = personPointOn(planet, enemyDir, ps, ps.losEyeHeight);
    const span = (dacos(Math.max(-1, Math.min(1, vecDot(mc, enemyDir)))));
    let best: { dir: Vec3; d: number } | null = null;
    for (const side of [1, -1]) {
      const flank = offset(mc, enemyDir, span * STANDOFF.flankAlong, side * (STANDOFF.flankPeople * person) / planet.radius);
      for (const s of terrainSpotsNear(st, pi, flank, STANDOFF.spotPeople * person, eye)) {
        if (s.hides !== 'stand' && s.hides !== 'crouch') continue;
        const d = arc(pi, s.dir, flank);
        if (!best || d < best.d) best = { dir: s.dir, d };
      }
    }
    if (!best) return;   // no dead ground to go by: the standoff stands
    if (issue(man, { kind: 'move', planetIndex: pi, surfaceDir: best.dir }, 'task', tick + Math.round(STANDOFF.routeSeconds / PHYS_DT)) === false) return;
    op = { squadId: man.id, base: base.map(q => q.id), enemy: { planetIndex: pi, surfaceDir: enemyDir }, waypoint: best.dir, phase: 'route', since: tick };
    ops.push(op);
  }

  /** Is this squad the manoeuvre's (its squad or its base) while one is under way? The commander leaves it to the manoeuvre. */
  const holds = (squadId: number): boolean => !!op && (op.squadId === squadId || op.base.includes(squadId));

  return { step, ops, holds };
}
